import { Button } from '@/components/button';
import { Checkbox } from '@/components/form/checkbox';
import { Dropzone } from '@/components/form/dropzone';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/form/select';
import { Modal } from '@/components/modal';
import { HSeparator } from '@/components/separator';
import {
  deleteStudio,
  deleteTicket,
  setCustomFormModal,
  setEvent,
  updateChannelChecked,
  updateCustomerDataChecked,
  updateOrAddStudio,
  updateOrAddTicket,
} from '@/reducers/events';
import { clearErrors, getErrors } from '@/reducers/tickets';
import { AppDispatch } from '@/root/store';
import SeatingBookingSystem from '@/routes/org/event/new/step-2/studio';
import { TicketComponent } from '@/routes/org/event/new/step-2/ticket';
import { ShowEvent } from '@/services/events';
import { UpdateTicketInformation } from '@/services/tickets';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DocumentCheckIcon, EyeIcon } from '@heroicons/react/24/solid';
import { Select } from '@radix-ui/react-select';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

export function OrgEventNewStep2() {
  const defaultTicketData = {
    id: null,
    code: '',
    saleable: '',
    name: '',
    description: '',
    quantity: '0',
    price: '',
    max_buy: 1,
    start_sell_at: '',
    end_sell_at: '',
    showtimes: [
      {
        id: null,
        value: null,
      },
    ],
    seat_layout: [
      [
        { id: 'A1', blocked: false, reserved: false },
        { id: 'A2', blocked: false, reserved: false },
      ],
      [
        { id: 'B1', blocked: false, reserved: false },
        { id: 'B2', blocked: false, reserved: false },
      ],
    ],
  };

  const { slug } = useParams();
  const [ticketData, setTicketData] = useState(defaultTicketData);
  const {
    event,
    tickets,
    studios,
    channels,
    customer_data,
    customFormModal,
    defaultCustomFormModal,
    onPublishClicked,
  } = useSelector((state: any) => state.event);
  const { errors } = useSelector((state: any) => state.tickets);
  const dispatch: AppDispatch = useDispatch();

  const [ticketModal, setTicketModal] = useState({
    state: false,
    label: '',
  });

  function onAddTicketClicked() {
    setTicketData(defaultTicketData);
    setTicketModal({
      state: true,
      label: 'create',
    });
  }

  function onEditTicketClicked(ticketId: number) {
    const ticket = tickets.filter((ticket: any) => ticket.id === ticketId)[0];

    setTicketData(ticket);
    setTicketModal({
      state: true,
      label: 'edit',
    });
  }

  function onEditStudioClicked(ticketId: number) {
    const ticket = studios.filter((ticket: any) => ticket.id === ticketId)[0];

    setTicketData(ticket);
    setTicketModal({
      state: true,
      label: 'edit',
    });
  }

  function onDeleteTicketClicked(ticketId: number) {
    dispatch(deleteTicket(ticketId));
  }

  function onDeleteStudioClicked(ticketId: number) {
    dispatch(deleteStudio(ticketId));
  }

  const setData = (e: any | string, element: string) => {
    const value = typeof e === 'string' ? e : e.target.value;

    setTicketData({ ...ticketData, [element]: value });
    dispatch(clearErrors());
  };

  useEffect(() => {
    dispatch(ShowEvent(slug));
  }, []);

  const updateChannel = (e: any) => {
    const { value, checked } = e.target;
    dispatch(updateChannelChecked({ id: Number(value), checked }));

    dispatch(clearErrors());
  };

  const updateEvent = (e: any) => {
    const { value } = e.target;

    dispatch(clearErrors());

    dispatch(
      setEvent({
        ...event,
        settings: {
          ...event.settings,
          fees: {
            ...event.settings.fees,
            assigned_type: value,
          },
        },
      })
    );
  };

  const updateCustomerData = (index: string, customerData: any) => {
    dispatch(
      updateCustomerDataChecked({ index, newCustomerData: customerData })
    );

    dispatch(clearErrors());
  };

  const closeModalTicket = () => {
    dispatch(clearErrors());
    setTicketData(defaultTicketData);

    setTicketModal({
      state: false,
      label: '',
    });
  };

  const updateTicket = () => {
    const validationErrors = validateTicketData(ticketData, event);
    if (validationErrors) {
      dispatch(
        getErrors({
          errorMsg: '',
          errors: validationErrors,
        })
      );

      return;
    }

    dispatch(
      updateOrAddTicket({
        ...ticketData,
        saleable_label:
          ticketData.saleable === 'ots' ? 'On The Spot' : 'Publik',
      })
    );

    setTicketModal({
      state: false,
      label: '',
    });
  };

  const updateStudio = () => {
    const validationErrors = validateTicketData(ticketData, event);

    if (validationErrors) {
      dispatch(
        getErrors({
          errorMsg: '',
          errors: validationErrors,
        })
      );

      return;
    }

    dispatch(updateOrAddStudio(ticketData));

    setTicketModal({
      state: false,
      label: '',
    });
  };

  const uploadSupportedImages = (e: any) => {
    e.forEach((image: File) => {
      dispatch(
        setEvent({
          ...event,
          supported_images: image,
        })
      );
    });
  };

  useEffect(() => {
    const channelIds = channels
      .filter((channel: any) => channel.checked)
      .map((channel: any) => channel.id);

    if (onPublishClicked.step === 'step-2') {
      let conditionalData = {};
      if (event.type !== 'cinema') {
        conditionalData = {
          tickets: tickets,
        };
      }

      dispatch(
        UpdateTicketInformation(event.id, {
          ...conditionalData,
          channels: channelIds,
          is_need_supported_images: Boolean(event.is_need_supported_images),
          supported_images: event?.supported_images,
          customer_data: customer_data,
          assigned_type: event?.settings?.fees?.assigned_type,
        })
      );
    }
  }, [onPublishClicked]);

  return (
    <>
      {event.type !== 'cinema' && (
        <TicketComponent
          ticketModal={ticketModal}
          closeModalTicket={closeModalTicket}
          errors={errors}
          ticketData={ticketData}
          setData={setData}
          updateTicket={updateTicket}
          tickets={tickets}
          onEditTicketClicked={(e: any) => {
            onEditTicketClicked(e);
          }}
          onAddTicketClicked={onAddTicketClicked}
          onDeleteTicketClicked={(e: any) => {
            onDeleteTicketClicked(e);
          }}
        />
      )}

      {/* ticket & studio based on event type */}

      {event.type === 'cinema' && <SeatingBookingSystem event={event} />}

      <FormLabel
        label={'Gambar pendukung'}
        errorText={errors.is_need_supported_images}
        inline
      >
        <Checkbox
          onChange={() =>
            dispatch(
              setEvent({
                ...event,
                is_need_supported_images: !event.is_need_supported_images,
              })
            )
          }
          checked={event.is_need_supported_images}
        />
      </FormLabel>

      {event.is_need_supported_images && (
        <div>
          <FormLabel
            label='Gambar pendukung'
            errorText={errors.supported_images}
            required
          >
            <Dropzone
              maxFiles={1}
              onChange={(e) => uploadSupportedImages(e)}
              previewUrl={event.supported_image_url}
            />
          </FormLabel>
        </div>
      )}

      <HSeparator />

      <div className={'grid grid-cols-2 gap-2'}>
        <div className='flex flex-col gap-6'>
          <p className='text-base font-medium text-danger'>Biaya Admin</p>
          <FormLabel
            label={'Biaya admin normal: Rp5.000'}
            errorText={errors.assigned_type}
          >
            <div className='flex flex-col gap-3'>
              <FormLabel label={'Dibebankan ke pembeli'} inline>
                <Checkbox
                  value={'full-guest'}
                  checked={
                    event?.settings?.fees?.assigned_type === 'full-guest'
                  }
                  onChange={(e: any) => {
                    updateEvent(e);
                  }}
                />
              </FormLabel>
              <FormLabel label={'Dibebankan ke penyelenggara'} inline>
                <Checkbox
                  value={'full-org'}
                  checked={event?.settings?.fees?.assigned_type === 'full-org'}
                  onChange={(e: any) => {
                    updateEvent(e);
                  }}
                />
              </FormLabel>
              <FormLabel
                label={'Dibebankan 50% ke pembeli dan 50% ke penyelenggara'}
                inline
              >
                <Checkbox
                  value={'fifty-fifty'}
                  checked={
                    event?.settings?.fees?.assigned_type === 'fifty-fifty'
                  }
                  onChange={(e: any) => {
                    updateEvent(e);
                  }}
                />
              </FormLabel>
            </div>
          </FormLabel>
        </div>
      </div>

      <HSeparator />

      <div className={'grid grid-cols-2 gap-2'}>
        <div className='flex flex-col gap-6'>
          <p className='text-base font-medium text-danger'>Metode pembayaran</p>
          <div className='flex flex-col gap-3'>
            {/*<p className='text-sm font-medium text-neutral-70'>E-money</p>*/}
            <div className='flex flex-wrap gap-6'>
              {channels.map((channel: any, index: string) => (
                <FormLabel label={channel.name} inline key={index}>
                  <Checkbox
                    checked={channel.checked}
                    value={channel.id}
                    onChange={(e: any) => {
                      updateChannel(e);
                    }}
                  />
                </FormLabel>
              ))}
            </div>
            <p className='block text-xs text-danger'>{errors.channels}</p>
          </div>
        </div>
      </div>

      <HSeparator />

      <Modal
        show={customFormModal.modal}
        onRequestClose={() =>
          dispatch(setCustomFormModal({ ...customFormModal, modal: false }))
        }
        title='Custom Form Data Pembeli'
        body={
          <>
            <div className='flex flex-wrap gap-2'>
              <FormLabel
                label='Gambar Pendukung (Opsional)'
                className='flex-grow'
                errorText={errors.media}
              >
                <Dropzone
                  onChange={(e) => {
                    e.forEach((image: File) => {
                      const reader = new FileReader();

                      reader.onloadend = () => {
                        dispatch(
                          setCustomFormModal({
                            ...customFormModal,
                            media: reader.result,
                          })
                        );
                      };

                      reader.readAsDataURL(image);
                    });
                  }}
                  maxFiles={1}
                  previewUrl={customFormModal?.media_url}
                />
              </FormLabel>
              <FormLabel
                label='Nama kolom'
                required
                className='flex-grow'
                errorText={errors.title}
              >
                <Input
                  type='text'
                  onChange={(e: any) => {
                    dispatch(
                      setCustomFormModal({
                        ...customFormModal,
                        label: e.target.value,
                      })
                    );
                  }}
                  value={customFormModal.label}
                  required
                />
              </FormLabel>
              <FormLabel
                label='Tipe kolom'
                required
                className='flex-grow'
                errorText={errors.title}
              >
                <Select
                  onValueChange={(value) => {
                    dispatch(
                      setCustomFormModal({ ...customFormModal, type: value })
                    );
                  }}
                  value={customFormModal.type}
                >
                  <SelectTrigger>
                    <SelectValue defaultValue='text' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={'text'}>Teks</SelectItem>
                      <SelectItem value={'select'}>Pilihan Ganda</SelectItem>
                      <SelectItem value={'date'}>Kalender</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </FormLabel>
              {customFormModal.type === 'select' && (
                <FormLabel
                  label='Opsi'
                  required
                  className='flex-grow'
                  errorText={errors.title}
                >
                  <Input
                    type='text'
                    onChange={(e: any) => {
                      dispatch(
                        setCustomFormModal({
                          ...customFormModal,
                          possible_values: e.target.value?.split(','),
                        })
                      );
                    }}
                    value={customFormModal?.possible_values?.join(',')}
                    placeholder={'opsi1,opsi2,opsi3,...dst'}
                    required
                  />
                </FormLabel>
              )}
            </div>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() =>
                dispatch(
                  setCustomFormModal({ ...customFormModal, modal: false })
                )
              }
              leftIcon={<XMarkIcon />}
            >
              Tidak
            </Button>
            <Button
              onClick={() => {
                updateCustomerData(customFormModal.index, {
                  ...customFormModal,
                  checked: true,
                });
                dispatch(
                  setCustomFormModal({ ...customFormModal, modal: false })
                );
              }}
              variant='primary'
              leftIcon={<DocumentCheckIcon />}
            >
              Simpan
            </Button>
          </div>
        }
      />

      <div className={'grid grid-cols-2 gap-2'}>
        <div className='flex flex-col gap-6'>
          <p className='text-base font-medium text-danger'>Data pembeli</p>
          <div className='flex flex-col gap-3'>
            {/*<p className='text-sm font-medium text-neutral-70'>E-money</p>*/}
            <div className='flex flex-wrap gap-6'>
              {customer_data.map((customerData: any, index: string) => (
                <FormLabel label={customerData.label} inline key={index}>
                  <Checkbox
                    disabled={customerData.mandatory}
                    checked={customerData.checked}
                    onChange={(e: any) => {
                      updateCustomerData(index, {
                        ...customerData,
                        checked: e.target.checked,
                      });
                    }}
                    value={customerData.id}
                  />
                  {customerData.id === 8 && (
                    <Button
                      size={'sm'}
                      variant={'neutral'}
                      rightIcon={<EyeIcon />}
                      onClick={() => {
                        dispatch(
                          setCustomFormModal({
                            ...customerData,
                            modal: true,
                            index: index,
                          })
                        );
                      }}
                    />
                  )}
                </FormLabel>
              ))}
            </div>
            <Button
              variant={'neutral'}
              onClick={() =>
                dispatch(
                  setCustomFormModal({ ...defaultCustomFormModal, modal: true })
                )
              }
              leftIcon={<PlusIcon />}
            >
              Tambah Form
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

const validateTicketData = (ticketData: any, event: any) => {
  const errors: any = {};

  // Validate name
  if (!ticketData.name.trim()) {
    errors.name = 'Nama tiket harus diisi.';
  }

  if (!ticketData.saleable.trim()) {
    errors.saleable = 'Visibilitas harus diisi.';
  }

  if (!ticketData.code.trim()) {
    errors.code = 'Kode harus diisi.';
  }

  // Validate description
  if (
    ticketData.description &&
    (ticketData.description.length < 5 || ticketData.description.length > 400)
  ) {
    errors.description = 'Deskripsi harus antara 5 hingga 400 karakter.';
  }

  if (event.type !== 'cinema') {
    // Validate quantity
    if (
      !ticketData.quantity ||
      isNaN(ticketData.quantity) ||
      Number(ticketData.quantity) <= 0
    ) {
      errors.quantity = 'Kuantitas harus angka positif.';
    }
  }

  // Validate quantity
  if (
    !ticketData.max_buy ||
    isNaN(ticketData.max_buy) ||
    Number(ticketData.max_buy) <= 0
  ) {
    errors.max_buy = 'Maksimal pembelian minimal 1 dan harus angka positif.';
  }

  // Validate price
  if (
    !ticketData.price ||
    isNaN(ticketData.price) ||
    Number(ticketData.price) < 0
  ) {
    errors.price = 'Harga harus diisi dengan angka valid.';
  }

  if (event.type === 'cinema') {
    // Validate showtimes
    if (
      ticketData.showtimes.length === 0 ||
      ticketData.showtimes.filter(
        (showtime: any) => !showtime.value || showtime.value.trim() === ''
      ).length > 0
    ) {
      errors.showtimes = 'Jam tayang harus diisi.';
    }

    let valueSeats = ticketData.seat_layout.reduce(
      (total: any, row: any) => total + row.length,
      0
    );
    // Validate seats
    if (!valueSeats) {
      errors.seat_layout = 'Kursi harus diisi minimal 1 kursi.';
    }
  }

  // Validate date fields
  const startDate = new Date(ticketData.start_sell_at);
  const endDate = new Date(ticketData.end_sell_at);

  if (!ticketData.start_sell_at) {
    errors.start_sell_at = 'Tanggal mulai dijual wajib diisi.';
  } else if (isNaN(startDate.getTime())) {
    errors.start_sell_at = 'Tanggal mulai tidak valid.';
  }

  if (!ticketData.end_sell_at) {
    errors.end_sell_at = 'Tanggal berakhir wajib diisi.';
  } else if (isNaN(endDate.getTime())) {
    errors.end_sell_at = 'Tanggal berakhir tidak valid.';
  } else if (startDate >= endDate) {
    errors.end_sell_at = 'Tanggal berakhir harus setelah tanggal mulai.';
  }

  return Object.keys(errors).length ? errors : null;
};
